import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    StyledLabel,
    StyledTextInput,
    CheckboxIcon,
    StyledSelectInput,
} from "@aureskonnect/react-ui";
import { AvGroup } from "availity-reactstrap-validation";
import { toast } from "react-toastify";
import classnames from "classnames";
import { useSnapshot } from "valtio";
import { Select } from "antd";
import { clearMultiCustomSelectionData } from "@components/Common/MultiCustomSelect/store";
import { clearData } from "@components/Common/MultiCustomCreatableSelection/store";
import {
    store as device,
    setIsActionsClicked,
    setActions,
    setIsEdited,
    setIsPlusIconClicked,
} from "./store";
import "@assets/swal.css";
import "@assets/theme.css";
import "react-toastify/dist/ReactToastify.css";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";
import {
    createDataVilleAmount,
    INITIAL_CITY_AMOUNT_FORM_DATA,
    INITIAL_VALIDATION,
    validateAndSubmit,
    formatCityAmountLivraisonData,
} from "./hooks";
import {
    CountryPostalCodes,
    fetchPays,
    getShopVatList,
    resolveDefaultTva,
    resolveTvaFromList,
    Tranche,
    SelectOption,
    CountryOption,
    PostalCodeOption,
    postalCodeFilterOption,
    scrollToFirstError,
} from "../shared";
import { PostCity } from "../City/hooks";
import ErrorToast from "@components/Common/ErrorTost";
import { setOrders, store as shopStore } from "../../store";
import { useFetchGlobal } from "../../../../../../api/hooks";
import { store as storeGlobal } from "@components/VerticalLayout/store";
import ErrorMessage from "../ErrorMessage";
import FormLayout from "../FormLayout";
import TranchesManager, { rebuildTranches } from "../TranchesManager";

type CityAmountFormType = {
    isAddCityAmount: boolean;
    setDataIsUpdated: React.Dispatch<React.SetStateAction<boolean | number>>;
    selectedRows: Record<string, unknown>[];
    setLocalFilterActive: (v: boolean) => void;
    setIsAddCityAmount: (v: boolean) => void;
    isAddNewDeviseButtonClicked: boolean;
    setSelectedRows: (v: Record<string, unknown>[]) => void;
    selectedRowShops: Record<string, unknown>[];
    cityData: { data: Record<string, unknown>[] };
};

export default function CityAmountForm({
    setIsAddCityAmount,
    isAddCityAmount,
    setDataIsUpdated,
    selectedRows,
    setLocalFilterActive,
    isAddNewDeviseButtonClicked,
    setSelectedRows,
    selectedRowShops,
    cityData,
}: CityAmountFormType) {
    const { t } = useTranslation();
    const { franchiseID } = useSnapshot(storeGlobal);
    const { fetchGlobal } = useFetchGlobal();
    const pays: CountryOption[] = useSelector(
        (state: RootState) => state.global.pays
    );
    const listCode = useMemo(
        () =>
            (cityData?.data ?? []).map(
                (el: Record<string, unknown>) =>
                    `${el[t("Postal code")] || el["Code Postal"]}_${
                        el[t("City")] || el["Ville"]
                    }`
            ),
        [cityData?.data, t]
    );

    const { isActionsClicked, isEdited } = useSnapshot(device);
    const i18nextLng = localStorage.getItem("i18nextLng") || "fr";
    const [
        isClickedBouttonValider,
        setIsClickedBouttonValider,
    ] = React.useState<boolean>(true);
    const [cityFormData, setCityFormData] = React.useState<{
        codePostal: string;
        city: string;
        tva: string;
        country: string;
    }>(INITIAL_CITY_AMOUNT_FORM_DATA);
    const [validation, setValidation] = React.useState<{
        inputCode: boolean;
        inputCity: boolean;
        inputTva: boolean;
        selectCountry: boolean;
        isExistingCodePostal: boolean;
        isTranchesEmpty: boolean;
    }>(INITIAL_VALIDATION);
    const [countryList, setCountryList] = React.useState<CountryOption[]>();
    const [title, setTitle] = React.useState<string>("");
    const [isModalOpened, setIsModalOpened] = React.useState<boolean>(false);
    const [code, setCode] = React.useState<PostalCodeOption[]>([]);
    const [tranches, setTranches] = useState<Tranche[]>([]);
    const listTva = useMemo(
        () =>
            getShopVatList(selectedRowShops).map((el) => ({
                label: el.value,
                value: el.id,
            })),
        [selectedRowShops]
    );
    async function addVille() {
        if (
            !resolveTvaFromList(
                cityFormData.tva,
                getShopVatList(selectedRowShops)
            )
        ) {
            ErrorToast(t("Invalid VAT rate"));
            return;
        }
        const localStCityLivraison = formatCityAmountLivraisonData(
            cityData.data
        );
        const data = createDataVilleAmount(
            localStCityLivraison,
            cityFormData,
            tranches,
            code,
            selectedRowShops,
            franchiseID,
            false,
            selectedRows
        );
        setIsClickedBouttonValider(false);
        try {
            const responseData = await PostCity(data, fetchGlobal);
            if (responseData.error) {
                setDataIsUpdated!(true);
                throw Error("error");
            }

            const updatedDataPrice = {
                ...(shopStore.orders.dataPrice || {}),
                STVilleLivraisonMontant: data.data,
            };

            setOrders({ dataPrice: updatedDataPrice });
            toast.success(`${t("City added successfully")!}`, {
                position: toast.POSITION.TOP_CENTER,
                theme: "colored",
                icon: <CheckboxIcon height={25} width={25} fill="white" />,
            });
            setDataIsUpdated!(true);
            setIsEdited(false);
            setLocalFilterActive(false);
            setCode([]);
            clearData();
            clearMultiCustomSelectionData();
            setIsClickedBouttonValider(true);
            setTranches([]);
        } catch (e) {
            ErrorToast(`${t("There's an error")!}`);
        }
    }

    async function editVille() {
        if (!isEdited) {
            setIsModalOpened(true);
        } else {
            if (
                !resolveTvaFromList(
                    cityFormData.tva,
                    getShopVatList(selectedRowShops)
                )
            ) {
                ErrorToast(t("Invalid VAT rate"));
                return;
            }
            const localStCityLivraison = formatCityAmountLivraisonData(
                cityData.data
            );
            const data = createDataVilleAmount(
                localStCityLivraison,
                cityFormData,
                tranches,
                code,
                selectedRowShops,
                franchiseID,
                true,
                selectedRows
            );
            try {
                const responseData = await PostCity(data, fetchGlobal);
                if (responseData.error) {
                    setDataIsUpdated!(true);
                    throw Error("error");
                }

                const updatedDataPrice = {
                    ...(shopStore.orders.dataPrice || {}),
                    STVilleLivraisonMontant: data.data,
                };
                setOrders({ dataPrice: updatedDataPrice });
                toast.success(`${t("modification made successfully")!}`, {
                    position: toast.POSITION.TOP_CENTER,
                    theme: "colored",
                    icon: <CheckboxIcon height={25} width={25} fill="white" />,
                });
                setDataIsUpdated!(true);
                setIsEdited(false);
                setLocalFilterActive(false);
                setCode([]);
                clearData();
                setIsAddCityAmount(!isAddCityAmount);
                clearMultiCustomSelectionData();
                setValidation((prev) => ({
                    ...prev,
                    inputCode: false,
                    inputTva: false,
                    selectCountry: false,
                    inputCity: false,
                }));
                setIsPlusIconClicked(false);
                setIsClickedBouttonValider(true);
                setTranches([]);
            } catch (e) {
                ErrorToast(`${t("There's an error")!}`);
                setIsClickedBouttonValider(true);
            }
        }
    }

    const updateValidation = useCallback((field: string, value: boolean) => {
        setValidation((prev) => ({
            ...prev,
            [field]: value,
        }));
    }, []);

    const handleTranchesChanged = useCallback(() => {
        setIsEdited(true);
        updateValidation("isTranchesEmpty", false);
    }, [updateValidation]);

    const handleCountryChange = useCallback(
        (e: SelectOption | null) => {
            setCityFormData((prev) => ({
                ...prev,
                country: e?.label || "",
            }));
            updateValidation("selectCountry", false);
            setIsEdited(true);
            setIsClickedBouttonValider(true);
        },
        [updateValidation]
    );

    const handlePostalCodeChange = useCallback(
        (value: string | null, e: PostalCodeOption | PostalCodeOption[]) => {
            const option = Array.isArray(e) ? e[0] : e;

            if (value !== null && option) {
                const label = String(option.label ?? "");

                const [postalCode, cityName] = label.split(" - ");

                // 🚨 BLOQUANT FIX
                if (!postalCode || !cityName) {
                    ErrorToast("Invalid postal code format");
                    return;
                }

                const trimmedCode = postalCode.trim();
                const trimmedCity = cityName.trim();

                setCityFormData((prev) => ({
                    ...prev,
                    codePostal: trimmedCode,
                    city: trimmedCity,
                }));

                updateValidation(
                    "isExistingCodePostal",
                    listCode.includes(`${trimmedCode}_${trimmedCity}`)
                );
            } else {
                setCityFormData((prev) => ({
                    ...prev,
                    codePostal: "",
                    city: "",
                }));
            }

            setIsEdited(true);
            setValidation((prev) => ({ ...prev, inputCode: false }));
            updateValidation("inputCity", false);
            setIsClickedBouttonValider(true);
        },
        [listCode, updateValidation]
    );
    const handleTvaChange = useCallback(
        (e: SelectOption | null) => {
            setCityFormData((prev) => ({
                ...prev,
                tva: e?.label || "",
            }));
            setIsEdited(true);
            updateValidation("inputTva", false);
            setIsClickedBouttonValider(true);
        },
        [updateValidation]
    );

    const handleCancel = useCallback(() => {
        if (isEdited) {
            setIsActionsClicked(!isActionsClicked);
            setActions(() => {
                setIsEdited(false);
                setSelectedRows([]);
                setIsAddCityAmount(false);
            });
        } else {
            setSelectedRows([]);
            setIsAddCityAmount(false);
        }
    }, [isEdited, isActionsClicked, setIsAddCityAmount, setSelectedRows]);

    const handleValidate = useCallback(() => {
        const isFormValid = validateAndSubmit(
            cityFormData,
            validation,
            updateValidation,
            tranches
        );
        if (!isFormValid) {
            scrollToFirstError();
        }
        if (isFormValid) {
            if (selectedRows.length === 0) {
                addVille();
            } else {
                editVille();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cityFormData, validation, tranches, selectedRows]);

    useEffect(() => {
        if (selectedRows.length > 0) {
            setTitle(t("Modify a city + amount"));
            const row = selectedRows[0];
            setCityFormData((prev) => ({
                ...prev,
                codePostal: String(
                    row[t("Postal code")] || row["Code Postal"] || ""
                ),
                city: String(row[t("City")] || row["Ville"] || ""),
                tva: row["Tva"] ? String(row["Tva"]) : "",
                country: String(row[t("Country")] || ""),
            }));
            setTranches(
                rebuildTranches((row.tiers || row.tranches || []) as Tranche[])
            );
        } else {
            setTitle(t("Add a city + amount"));
            setCityFormData({
                ...INITIAL_CITY_AMOUNT_FORM_DATA,
                tva: resolveDefaultTva(getShopVatList(selectedRowShops)),
            });
            setTranches([]);
            setValidation(INITIAL_VALIDATION);
            setIsEdited(false);
            setIsClickedBouttonValider(true);
        }
        fetchPays(pays, setCountryList).catch(() =>
            ErrorToast(t("Failed to load country or VAT data"))
        );
        // eslint-disable-next-line
    }, [isAddNewDeviseButtonClicked, selectedRows]);

    React.useEffect(() => {
        CountryPostalCodes(
            cityFormData.country,
            setCode,
            i18nextLng,
            fetchGlobal
        ).catch(() => ErrorToast(t("Failed to load postal codes")));
        // eslint-disable-next-line
    }, [cityFormData.country, setCode, i18nextLng]);

    return (
        <FormLayout
            title={title}
            onCancel={handleCancel}
            onValidate={handleValidate}
            isValidateDisabled={!isClickedBouttonValider}
            setDataIsUpdated={setDataIsUpdated}
            isModalOpened={isModalOpened}
            setIsModalOpened={setIsModalOpened}
            isAddClicked={isAddCityAmount}
            setIsAddClicked={setIsAddCityAmount}
            setEditedData={setSelectedRows}
            formClassName="card"
        >
            <AvGroup>
                <StyledLabel htmlFor="text" className="required__clz mt-3">
                    {t("Country")}
                </StyledLabel>
                <StyledSelectInput
                    data-testid="city-amount-country-select"
                    isClearable
                    autoFocus
                    noOptionsMessage={() => t("No options")}
                    options={countryList}
                    autocomplete="off"
                    className={classnames("", {
                        input__clz: validation.selectCountry,
                    })}
                    name="change"
                    onChange={handleCountryChange}
                    value={
                        cityFormData.country === ""
                            ? null
                            : {
                                  label: cityFormData.country,
                                  value: cityFormData.country,
                              }
                    }
                    placeholder={`${t("Select")}…`}
                    type="text"
                />
                {validation.selectCountry && (
                    <ErrorMessage message={t("Please select a country")} />
                )}
            </AvGroup>

            <AvGroup>
                <StyledLabel htmlFor="text" className="required__clz mt-3">
                    {t("Zip code")}
                </StyledLabel>
                <div>
                    <Select
                        data-testid="city-amount-postal-code-select"
                        style={{ width: "100%", fontSize: "14px" }}
                        options={code}
                        className={classnames("", {
                            input__clz: validation.inputCode,
                        })}
                        value={
                            cityFormData.codePostal && cityFormData.city
                                ? `${cityFormData.codePostal}_${cityFormData.city}`
                                : undefined
                        }
                        showSearch={true}
                        onChange={handlePostalCodeChange}
                        optionLabelProp="postalLabel"
                        filterOption={postalCodeFilterOption}
                    />
                </div>
                {validation.inputCode && (
                    <ErrorMessage message={t("Please select a  Zip code")} />
                )}
                {validation.isExistingCodePostal && (
                    <ErrorMessage
                        message={
                            t("This city with this postal code already exists")!
                        }
                    />
                )}
            </AvGroup>

            <AvGroup>
                <StyledLabel htmlFor="text" className="required__clz mt-3">
                    {t("City")}
                </StyledLabel>
                <StyledTextInput
                    data-testid="city-amount-city-input"
                    autocomplete="off"
                    name={t("City")}
                    value={cityFormData.city}
                    placeholder={t("City")}
                    className={classnames("not-allowed-input__clz", {
                        input__clz: validation.inputCity,
                    })}
                    type="text"
                />
                {validation.inputCity && (
                    <ErrorMessage message={t("Please select a City")} />
                )}
            </AvGroup>

            <AvGroup>
                <StyledLabel htmlFor="text" className="required__clz mt-3">
                    {t("TVA")}
                </StyledLabel>
                <StyledSelectInput
                    data-testid="city-amount-tva-select"
                    isClearable
                    noOptionsMessage={() => t("No options")}
                    options={listTva}
                    autocomplete="off"
                    className={classnames("", {
                        input__clz: validation.inputTva,
                    })}
                    name="tva"
                    onChange={handleTvaChange}
                    value={
                        cityFormData.tva
                            ? {
                                  label: cityFormData.tva,
                                  value: cityFormData.tva,
                              }
                            : null
                    }
                    placeholder={`${t("Select")}…`}
                    type="text"
                    maxMenuHeight="80px"
                />
                {validation.inputTva && (
                    <ErrorMessage message={t("Please select a vat rate")} />
                )}
            </AvGroup>

            {/* Tranches section */}
            <AvGroup>
                <StyledLabel htmlFor="text" className="required__clz mt-3">
                    {t("Order amount fee tiers")}
                </StyledLabel>
                <TranchesManager
                    tranches={tranches}
                    setTranches={setTranches}
                    onChanged={handleTranchesChanged}
                    validationError={validation.isTranchesEmpty}
                />
            </AvGroup>
        </FormLayout>
    );
}
